import { PaginatedResponse } from '@/types/apiTypes';
import { PresignResponse } from '@/types/presign';
import type { StagedAuctionFeeChange } from '@/components/AuctionCreate/Financials/AuctionFees/stagedAuctionFeeChanges';

export type StatusType = 'success' | 'warning' | 'info' | null;

// Backend enum values from SlbAuctionStatus
export enum SlbAuctionStatus {
    CLOSED = 'Closed',
    DELETED = 'Deleted',
    NEW = 'New',
    PUBLISHED = 'Published',
    UNPUBLISHED = 'Unpublished',
}

export const closedSlbRecordStatuses: SlbAuctionStatus[] = [SlbAuctionStatus.CLOSED, SlbAuctionStatus.DELETED];

export const deletableAuctionStatuses: SlbAuctionStatus[] = [
    SlbAuctionStatus.NEW,
    SlbAuctionStatus.PUBLISHED,
    SlbAuctionStatus.UNPUBLISHED,
];
// Backend enum values from BiddingType
export enum BiddingType {
    LIVE = 'Live',
    TIMED_BIDDING = 'TimedBidding',
}

// ISO 4217 currency codes
// @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#currency_2
export enum CurrencyCode {
    EUR = 'EUR',
    GBP = 'GBP',
    USD = 'USD',
}

// Backend enum values from ReserveType
export enum ReserveType {
    HOUSE_BID = 'HouseBid',
    STANDARD = 'Standard',
}

// Backend enum values from AcceptedPaymentType
export enum AcceptedPaymentType {
    AMEX = 'AMEX',
    CASH = 'CASH',
    CHECK = 'CHECK',
    DISCOVER = 'DISCOVER',
    MASTERCARD = 'MASTERCARD',
    MONEY_ORDER = 'MONEY_ORDER',
    VISA = 'VISA',
    WIRE_TRANSFER = 'WIRE_TRANSFER',
}

// Backend enum values from RegistrationTier
export enum RegistrationTier {
    TIER1 = 'Tier1',
    TIER2 = 'Tier2',
    TIER3 = 'Tier3',
    TIER4 = 'Tier4',
}

export type ApprovalCriteriaOption = {
    label: string;
    value: RegistrationTier;
};

// Backend marketplace codes and their frontend display labels
export const MARKETPLACES = {
    GAP_BSC: { code: 'GAP-BSC', label: 'BidSpotter' },
    GAP_PBWL: { code: 'GAP-PBWL', label: 'Proxibid White Label' },
    LA_MP: { code: 'LA-MP', label: 'LiveAuctioneers' },
    PN_CEG: { code: '3P-CEG', label: 'Construction Equipment Guide' },
    PN_FAST: { code: '3P-FAST', label: 'Fastline.com' },
    PN_MPETE: { code: '3P-MPETE', label: 'Machinery Pete' },
    PN_TBN: { code: '3P-TBN', label: 'TractorByNet' },
    PXB_MP: { code: 'PXB-MP', label: 'Proxibid' },
} as const;

export type DestinationMarketplaceValue = (typeof MARKETPLACES)[keyof typeof MARKETPLACES]['code'];

// PB-MP is classic Proxibid, not White Label. 3P-DM is unused DOTmed.
// Both stay off MARKETPLACES so pickers cannot select them; writers omit them.
export const REMOVED_DESTINATION_MARKETPLACE_CODES = ['PB-MP', '3P-DM'] as const;

export const ATG_XL_MARKETPLACE_CODES = [
    MARKETPLACES.GAP_BSC.code,
    MARKETPLACES.GAP_PBWL.code,
    MARKETPLACES.LA_MP.code,
    MARKETPLACES.PXB_MP.code,
] as const;

export type AtgXlMarketplaceValue = (typeof ATG_XL_MARKETPLACE_CODES)[number];

export const PARTNER_NETWORK_MARKETPLACES = [
    { ...MARKETPLACES.PN_MPETE, translationKey: 'partnerMpete' },
    { ...MARKETPLACES.PN_CEG, translationKey: 'partnerCeg' },
    { ...MARKETPLACES.PN_TBN, translationKey: 'partnerTbn' },
    { ...MARKETPLACES.PN_FAST, translationKey: 'partnerFast' },
] as const;

// Nested types for auction requests and responses
/** @apiSchema AuctionAddress */
export type AuctionAddress = {
    address?: string;
    auctionHouseEmail?: string;
    auctionHouseTelephone?: string;
    auctionHouseWebAddress?: string;
    city?: string;
    country?: string;
    postalCode?: string;
    state?: string;
};

export type AuctionDetails = {
    auctionImageUrl?: string;
    auctionPageUrl?: string;
    auctionStatus: SlbAuctionStatus;
    description?: string;
    originatingCategoryPlatform?: string;
    shippingInstructions?: string;
    termsAndConditions?: string;
    title?: string;
    universalCategory?: string;
};

export type Increment = {
    high?: string;
    incrementValue?: string;
    liveIncrementValues: string[];
    low: string;
};

export type DateRange = {
    utcEndTime?: string;
    utcStartTime: string;
};

export type BiddingDetails = {
    biddingType: BiddingType;
    currency: CurrencyCode;
    extensionTimeInSeconds?: number;
    increment: Increment[];
    isPiecemeal: boolean;
    liveBiddingDates: DateRange[];
    livePreviewDates: DateRange[];
    reserveType: ReserveType;
    timezone: string;
    utcEndsFrom?: string;
    utcStartTime?: string;
};

export type TaxDetails = {
    buyersPremium: number;
    buyersPremiumCeiling?: number;
    buyersPremiumTax?: number;
    salesTax?: number;
};

// Request type for creating and updating auctions (AuctionInfo from backend)
/** @apiSchema SlbAuctionResponse */
export type AuctionInfo = {
    atgAuctionHouseId: string;
    atgAuctionHouseName?: string;
    atgAuctionId?: number;
    atgGlobalAuctionId: string;
    atgLotIds?: number[];
    atgSourcePlatformCode?: string;
    auctionAddress: AuctionAddress;
    auctionDetails: AuctionDetails;
    biddingDataModified: boolean;
    biddingDetails: BiddingDetails;
    deleted: boolean;
    destinationMarketplaces: DestinationMarketplaceValue[];
    destinationPlatforms: string[];
    externalAuctionId?: number | null;
    rowVersion: number;
    taxDetails: TaxDetails;
    timestamp?: string;
    userIdFromSourcePlatform?: number;
};

export type TransformationItem = {
    code: number;
    description: string;
    path: string;
    value: string;
};

export type LotSummary = {
    open: number;
    sold: number;
    passed: number;
    openWithBids: number;
    total: number;
    /**
     * Non-deleted lots with at least one photo on Manage Photos: a non-cancelled image attachment.
     * SLB lot image URLs do not count, since the Manage grid never shows them.
     * The service always sends it (its OpenAPI schema marks it required). It is optional here on
     * purpose: a UI deployed ahead of the service gets no value, and reads that as 0 (WBPR-5112).
     */
    withImages?: number;
};

// Response type (AuctionResponse from backend V3 API)
/** @apiSchema AuctionResponse */
export type AuctionResponse = {
    acceptedPaymentTypes: AcceptedPaymentType[];
    auction: AuctionInfo;
    deleteAllowed: boolean;
    isPrivate?: boolean;
    /**
     * Linked-auction summary (SP2-5202). Absent/null = unlinked.
     * `sharedBidderCount` may stay null until SP2-5260 — never fabricate 0 in UI.
     */
    linkGroupId?: string | null;
    linkGroupName?: string | null;
    localCategoryId?: string;
    lotSummary: LotSummary;
    blockInternationalBidders?: boolean;
    paddleSeed: number;
    perLotGrouping?: number;
    presignResponse?: PresignResponse;
    registrationTier?: RegistrationTier;
    customRegistrationMessage?: string | null;
    sharedBidderCount?: number | null;
    staggerIntervalSeconds?: number;
    validationResults?: TransformationItem[];
};

export const mockAuctions: PaginatedResponse<AuctionResponse> = {
    data: [
        {
            acceptedPaymentTypes: [
                AcceptedPaymentType.VISA,
                AcceptedPaymentType.MASTERCARD,
                AcceptedPaymentType.DISCOVER,
            ],
            auction: {
                atgAuctionHouseId: 'test-house-id',
                atgAuctionHouseName: 'Test House',
                atgAuctionId: 1613582,
                atgGlobalAuctionId: 'test-id-1',
                atgLotIds: [1, 2, 3],
                atgSourcePlatformCode: 'TEST',
                auctionAddress: {
                    address: '123 Test St',
                    auctionHouseEmail: 'test@example.com',
                    auctionHouseTelephone: '555-1234',
                    auctionHouseWebAddress: 'https://example.com',
                    city: 'Test City',
                    country: 'US',
                    postalCode: '12345',
                    state: 'CA',
                },
                auctionDetails: {
                    auctionImageUrl: 'https://example.com/image1.jpg',
                    auctionPageUrl: 'https://example.com/auction1',
                    auctionStatus: SlbAuctionStatus.PUBLISHED,
                    description: 'Test description 1',
                    originatingCategoryPlatform: 'TEST',
                    shippingInstructions: 'Test shipping',
                    termsAndConditions: 'Test terms',
                    title: 'Test Auction 1',
                    universalCategory: 'TEST',
                },
                biddingDataModified: false,
                biddingDetails: {
                    biddingType: BiddingType.TIMED_BIDDING,
                    currency: CurrencyCode.USD,
                    extensionTimeInSeconds: 300,
                    increment: [
                        {
                            high: '100',
                            incrementValue: '5',
                            liveIncrementValues: ['5', '10', '25'],
                            low: '0',
                        },
                    ],
                    isPiecemeal: true,
                    liveBiddingDates: [],
                    livePreviewDates: [],
                    reserveType: ReserveType.STANDARD,
                    timezone: 'America/Denver',
                    utcEndsFrom: '2024-01-02T10:00:00Z',
                    utcStartTime: '2024-01-01T10:00:00Z',
                },
                deleted: false,
                destinationMarketplaces: [MARKETPLACES.PXB_MP.code, MARKETPLACES.GAP_BSC.code],
                destinationPlatforms: ['TEST'],
                rowVersion: 1,
                taxDetails: {
                    buyersPremium: 10.0,
                    buyersPremiumCeiling: 1000,
                    buyersPremiumTax: 2.0,
                    salesTax: 8.5,
                },
                timestamp: '2024-01-01T09:00:00Z',
                userIdFromSourcePlatform: 12345,
            },
            deleteAllowed: false,
            lotSummary: { open: 3, openWithBids: 0, passed: 0, sold: 0, total: 3 },
            paddleSeed: 1000,
            presignResponse: undefined,
            validationResults: undefined,
        },
    ],
    page: {
        pageCount: 1,
        pageNumber: 1,
        pageSize: 25,
        totalCount: 1,
    },
};

/** @apiRequestSchema AuctionApiRequest */
export type CreateAuctionRequest = {
    acceptedPaymentTypes: AcceptedPaymentType[];
    auctionAddress: AuctionAddress;
    auctionDetails: AuctionDetails;
    auctionTemplateId?: string;
    biddingDetails: BiddingDetails;
    destinationMarketplaces: DestinationMarketplaceValue[];
    image?: {
        mimeType: string;
        name: string;
        size: number;
    };
    blockInternationalBidders?: boolean;
    isPrivate: boolean;
    localCategoryId?: string;
    paddleSeed?: number;
    perLotGrouping?: number;
    registrationTier: RegistrationTier;
    customRegistrationMessage?: string | null;
    staggerIntervalSeconds?: number;
    taxDetails: TaxDetails;
};

export type DestinationMarketplace = 'GAP_BSC' | 'LIVE_AUCTIONEERS' | 'PXB-MP' | 'GAP-PBWL' | 'PB-MP'; // Add more as needed

export type TimezoneFormOption = {
    label: string;
    labelShort?: string;
    name?: string;
    utcOffset?: number;
    value?: string;
};

export type BidIncrementFormData = {
    minAmount: string;
    upToAmount: string;
};

// UI Form Type (for React Hook Form)
/* eslint-disable typescript-sort-keys/interface */
export type AuctionFormData = {
    // Template origin — set when auction is created from a template; drives server-side image copy
    auctionTemplateId?: string;

    // Auction Status Section (only used in Edit mode)
    auctionStatus?: {
        backendStatus?: SlbAuctionStatus;
        status: 'Disabled' | 'Pending' | 'Online';
    };

    // Event Management Section
    eventManagement: {
        auctionType: 'Live' | 'TimedBidding';
    };

    // Marketplace Section
    marketplace: {
        selectedMarketplaces: string[];
    };

    // Basic Information Section
    basics: {
        title: string;
        description: string;
        category: {
            label: string;
            value: string;
        } | null;
        approvalCriteria: ApprovalCriteriaOption | null;
        customRegistrationMessage: string;
        isPrivate: boolean;
        pendingThumbnail: File | null;
        /** True after the user removes the saved thumbnail and has not chosen a replacement. */
        removeExistingImage?: boolean;
    };

    // Location Section
    location: {
        address: string;
        city: string;
        state: {
            label: string;
            name: string;
            value: string;
        } | null;
        postalCode: string;
        country: string;
        email: string;
        phone: string;
    };

    // Date & Time Section
    dateTime: {
        endTime: string;
        startTime: string;
        timestamp: string | null;
        timezone: TimezoneFormOption | null;
        website: string;
        intervalTime: { label: string; value: string } | null;
        perIncrement: { label: string; value: string } | null;
        // Validity tracking for TimeHeader display (UI only, not submitted)
        isStartTimeValid?: boolean;
        isEndTimeValid?: boolean;
    };

    // Bid Increment Section
    bidIncrements: {
        increments: BidIncrementFormData[];
        section?: string; // Section-level validation errors (shown in callout)
    };

    // Registration Details Section
    registrationDetails: {
        blockInternationalBidders: boolean;
        paddleSeed: string;
    };

    // Financials Section
    financials: {
        buyersPremium: string;
        buyersPremiumCeiling: string | null;
        currency: {
            label: string;
            name: string;
            value: CurrencyCode;
            prefix: string;
        };
        salesTax: string | null;
        shippingInstructions: string;
        acceptedPaymentTypes: AcceptedPaymentType[];
    };

    // Link auction section (create wizard — post-create side effect; not in auction POST body)
    linkAuction: {
        linkGroupId: string | null;
        linkMode: 'none' | 'existing' | 'new';
        newGroupName: string;
    };

    feeOverrides: StagedAuctionFeeChange[];

    // Terms and Conditions Section
    termsAndConditions: {
        content: string;
    };
};
/* eslint-enable typescript-sort-keys/interface */

/* eslint-disable sort-keys/sort-keys-fix */
export const emptyAuctionFormData: AuctionFormData = {
    // Event Management Section
    eventManagement: {
        auctionType: 'TimedBidding',
    },

    // Marketplace Section
    // Marketplace Section — Proxibid default when cross-listing selection UI is off (CROSS_LISTING_BASICS)
    marketplace: {
        selectedMarketplaces: [MARKETPLACES.PXB_MP.code],
    },

    // Basic Information Section
    basics: {
        title: '',
        description: '',
        category: null,
        approvalCriteria: { label: 'Tier 1', value: RegistrationTier.TIER1 },
        customRegistrationMessage: '',
        isPrivate: false,
        pendingThumbnail: null,
    },

    // Location Section
    location: {
        address: '',
        city: '',
        state: null,
        postalCode: '',
        country: 'US',
        email: '',
        phone: '',
    },

    // Date & Time Section
    dateTime: {
        endTime: '',
        startTime: '',
        timestamp: null,
        timezone: null,
        website: '',
        intervalTime: { label: '1 Minute', value: '1' },
        perIncrement: { label: '1 Item', value: '1' },
        isStartTimeValid: false,
        isEndTimeValid: false,
    },

    // Bid Increment Section
    bidIncrements: {
        increments: [
            {
                minAmount: '',
                upToAmount: '',
            },
        ],
        section: undefined, // Section-level validation errors
    },

    // Registration Details Section
    registrationDetails: {
        blockInternationalBidders: false,
        paddleSeed: '',
    },

    // Financials Section
    financials: {
        buyersPremium: '',
        buyersPremiumCeiling: null,
        currency: { label: 'USD', name: 'usd', prefix: '$', value: CurrencyCode.USD },
        salesTax: null,
        shippingInstructions: '',
        acceptedPaymentTypes: [],
    },

    // Link auction section
    linkAuction: {
        linkGroupId: null,
        linkMode: 'none',
        newGroupName: '',
    },

    feeOverrides: [],

    // Terms and Conditions Section
    termsAndConditions: {
        content: '',
    },
};
/* eslint-enable sort-keys/sort-keys-fix */

export const emptyAuctionInfo: AuctionInfo = {
    atgAuctionHouseId: '',
    atgGlobalAuctionId: '',
    auctionAddress: {},
    auctionDetails: {
        auctionStatus: SlbAuctionStatus.NEW,
    },
    biddingDataModified: false,
    biddingDetails: {
        biddingType: BiddingType.TIMED_BIDDING,
        currency: CurrencyCode.USD,
        increment: [],
        isPiecemeal: false,
        liveBiddingDates: [],
        livePreviewDates: [],
        reserveType: ReserveType.STANDARD,
        timezone: '',
    },
    deleted: false,
    destinationMarketplaces: [],
    destinationPlatforms: [],
    rowVersion: 0,
    taxDetails: {
        buyersPremium: 0,
    },
};

export type LotTrackerAuctionState = {
    readonly isAuctionOnline: boolean;
    readonly isStarted: boolean;
};
